import { CodeRow, CodeItemGroup, IfNode, ConditionEntry, ConditionGroup, RuleNode } from '../types/codeIndex'
import { normalizeCodeItemReference } from './codeReference'
import { buildPositionableValues, getCodeRowPositionEnd, isPositionedCodeRow } from './compositeCodeUtils'

export type RowGroupSection = {
  key: string
  name: string | null
  rowIndices: number[]
}

export type CodeItemReferenceOptions = {
  code: string
  options: string[]
  rows?: Array<Pick<CodeRow, 'optionCode' | 'group' | 'valuePosition' | 'valuePositionEnd'>>
}

export type RuleValueReferenceIssue = {
  referencedCodeItemCode: string
  missingValue: string
  ruleId: string | null
  ruleDescription: string | null
  ruleLabel: string
  sourceCodeItemCode?: string
  sourceOptionCode?: string
  sourceRowId?: string
  sourceGroupIndex?: number
  sourceRowIndex?: number
}

export function escapeHtml(value: string | null | undefined): string {
  if (value === null || value === undefined) return ''
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

export function escapeHtmlForTextarea(value: string | null | undefined): string {
  return escapeHtml(value).replace(/\r?\n/g, '&#10;')
}

export function normalizeRowGroupName(value: string | null | undefined): string | null {
  const trimmed = String(value || '').trim()
  return trimmed ? trimmed : null
}

export function getBlockId(row: CodeRow, column: 'description' | 'availability'): string {
  if (column === 'description') return row.descriptionBlockId
  if (column === 'availability') return row.availabilityBlockId
  return row.descriptionBlockId
}

export function getColumnValue(row: CodeRow, column: 'description' | 'availability'): any {
  if (column === 'description') return row.description
  if (column === 'availability') return row.rule
  return row.description
}

export function makeBlockId(row: CodeRow, column: 'description' | 'availability', index: number): string {
  const prefix = column === 'description' ? 'desc' : 'avail'
  const base =
    (row.rowId && row.rowId.trim()) ||
    (row.optionCode && row.optionCode.trim()) ||
    `row-${index}`
  return `${base}__${prefix}__${index}`
}

export function collectBlockValues<T>(rows: CodeRow[], column: 'description' | 'availability'): Map<string, T | null> {
  const map = new Map<string, T | null>()
  for (const row of rows) {
    const key = getBlockId(row, column)
    if (!map.has(key) || row.rowId === key) {
      const val = getColumnValue(row, column) as T | null
      map.set(key, val)
    }
  }
  return map
}

export function normalizeBlocks(rows: CodeRow[], column: 'description' | 'availability', previousValues: Map<string, any>): CodeRow[] {
  const next = [...rows]
  const usedBlockIds = new Set<string>()
  let i = 0
  while (i < next.length) {
    const blockId = getBlockId(next[i], column)
    let j = i
    while (j + 1 < next.length && getBlockId(next[j + 1], column) === blockId) {
      j++
    }
    const leaderRow = next[i]
    // Block IDs are persisted properties. Keep the existing ID for the first contiguous
    // segment so ordinary row insertion does not create an unrelated Update change.
    // A new ID is only needed for a missing ID or a later segment of a split block.
    const newLeaderId = blockId && !usedBlockIds.has(blockId)
      ? blockId
      : makeBlockId(leaderRow, column, i)
    usedBlockIds.add(newLeaderId)
    if (blockId) usedBlockIds.add(blockId)
    const storedValue = previousValues.get(blockId)
    const value = storedValue !== undefined ? storedValue : getColumnValue(leaderRow, column)

    for (let k = i; k <= j; k++) {
      const newRow = { ...next[k] }
      if (column === 'description') {
        newRow.descriptionBlockId = newLeaderId
        newRow.description = value
      } else if (column === 'availability') {
        newRow.availabilityBlockId = newLeaderId
        newRow.rule = value
      }
      next[k] = newRow
    }
    i = j + 1
  }
  return next
}

export function syncRowPositions(rows: CodeRow[]): CodeRow[] {
  return rows.map((row, index) => (
    row.position === index
      ? row
      : { ...row, position: index }
  ))
}

export function buildRowGroupSections(rows: CodeRow[]): RowGroupSection[] {
  const sections: RowGroupSection[] = []
  let index = 0

  while (index < rows.length) {
    const normalizedGroupName = normalizeRowGroupName(rows[index]?.group)
    let endIndex = index

    while (
      endIndex + 1 < rows.length &&
      normalizeRowGroupName(rows[endIndex + 1]?.group) === normalizedGroupName
    ) {
      endIndex++
    }

    const rowIndices: number[] = []
    for (let offset = 0; offset <= endIndex - index; offset++) {
      rowIndices.push(index + offset)
    }

    sections.push({
      key: `section:${index}`,
      name: normalizedGroupName,
      rowIndices,
    })

    index = endIndex + 1
  }

  return sections
}

export function areRowIndicesInSameGroupSection(rows: CodeRow[], indices: number[]): boolean {
  if (indices.length === 0) return false

  const sortedIndices = [...indices].sort((a, b) => a - b)
  const firstIndex = sortedIndices[0]
  const lastIndex = sortedIndices[sortedIndices.length - 1]
  const expectedGroupName = normalizeRowGroupName(rows[firstIndex]?.group)

  for (let index = firstIndex; index <= lastIndex; index++) {
    if (normalizeRowGroupName(rows[index]?.group) !== expectedGroupName) {
      return false
    }
  }

  return true
}

export function splitBlocksAcrossRowGroups(rows: CodeRow[], column: 'description' | 'availability'): CodeRow[] {
  const next = [...rows]

  for (let index = 1; index < next.length; index++) {
    const previousGroupName = normalizeRowGroupName(next[index - 1]?.group)
    const currentGroupName = normalizeRowGroupName(next[index]?.group)

    if (previousGroupName === currentGroupName) continue

    const previousBlockId = getBlockId(next[index - 1], column)
    const currentBlockId = getBlockId(next[index], column)
    if (previousBlockId !== currentBlockId) continue

    let endIndex = index
    while (endIndex + 1 < next.length && getBlockId(next[endIndex + 1], column) === currentBlockId) {
      endIndex++
    }

    const leaderValue = getColumnValue(next[index], column)
    const newBlockId = makeBlockId(next[index], column, index)

    for (let sliceIndex = index; sliceIndex <= endIndex; sliceIndex++) {
      const nextRow = { ...next[sliceIndex] }
      if (column === 'description') {
        nextRow.descriptionBlockId = newBlockId
        nextRow.description = leaderValue
      } else {
        nextRow.availabilityBlockId = newBlockId
        nextRow.rule = leaderValue
      }
      next[sliceIndex] = nextRow
    }
  }

  return next
}

export function normalizeRowsForRowGroups(rows: CodeRow[]): CodeRow[] {
  const descriptionValues = collectBlockValues(rows, 'description')
  const availabilityValues = collectBlockValues(rows, 'availability')
  const normalizedDescriptionRows = normalizeBlocks(rows, 'description', descriptionValues)
  const normalizedAvailabilityRows = normalizeBlocks(normalizedDescriptionRows, 'availability', availabilityValues)
  const descriptionSplitRows = splitBlocksAcrossRowGroups(normalizedAvailabilityRows, 'description')
  const availabilitySplitRows = splitBlocksAcrossRowGroups(descriptionSplitRows, 'availability')
  return syncRowPositions(availabilitySplitRows)
}

export function moveRowWithinGroupSections(rows: CodeRow[], fromIndex: number, toIndex: number, nextGroupName: string | null): CodeRow[] {
  if (fromIndex === toIndex && normalizeRowGroupName(rows[fromIndex]?.group) === normalizeRowGroupName(nextGroupName)) {
    return syncRowPositions(rows)
  }
  if (fromIndex < 0 || fromIndex >= rows.length || toIndex < 0 || toIndex > rows.length) return syncRowPositions(rows)

  const next = [...rows]
  const [movedRow] = next.splice(fromIndex, 1)

  if (!movedRow) return syncRowPositions(rows)

  next.splice(toIndex, 0, {
    ...movedRow,
    group: normalizeRowGroupName(nextGroupName),
  })

  return normalizeRowsForRowGroups(next)
}

export function reorderRowsWithinGroup(rows: CodeRow[], fromIndex: number, toIndex: number): CodeRow[] {
  if (fromIndex === toIndex) return rows
  if (fromIndex < 0 || fromIndex >= rows.length || toIndex < 0 || toIndex >= rows.length) return rows

  const descriptionValues = collectBlockValues<string>(rows, 'description')
  const availabilityValues = collectBlockValues(rows, 'availability')
  const next = [...rows]
  const [movedRow] = next.splice(fromIndex, 1)

  if (!movedRow) return rows

  next.splice(toIndex, 0, movedRow)

  const normalizedDescriptionRows = normalizeBlocks(next, 'description', descriptionValues)
  const normalizedAvailabilityRows = normalizeBlocks(normalizedDescriptionRows, 'availability', availabilityValues)
  const descriptionSplitRows = splitBlocksAcrossRowGroups(normalizedAvailabilityRows, 'description')
  const availabilitySplitRows = splitBlocksAcrossRowGroups(descriptionSplitRows, 'availability')
  return syncRowPositions(availabilitySplitRows)
}

export function reorderCodeItemGroups(groups: CodeItemGroup[], fromIndex: number, toIndex: number): CodeItemGroup[] {
  if (fromIndex === toIndex) return groups
  if (fromIndex < 0 || fromIndex >= groups.length || toIndex < 0 || toIndex >= groups.length) return groups

  const next = [...groups]
  const [movedGroup] = next.splice(fromIndex, 1)

  if (!movedGroup) return groups

  next.splice(toIndex, 0, movedGroup)

  let runningStart = 1
  const repositionedGroups = next.map(group => {
    const [start, end] = group.codeItemPos.posRange
    const len = Math.max(1, end - start + 1)
    const nextStart = runningStart
    const nextEnd = nextStart + len - 1

    runningStart = nextEnd + 1

    return {
      ...group,
      codeItemPos: {
        ...group.codeItemPos,
        posRange: [nextStart, nextEnd] as [number, number],
        label: `${group.codeItemPos.code} [${nextStart}-${nextEnd}]`,
      },
    }
  })

  return reindexCodeItemGroups(repositionedGroups)
}

export function removeGroupByIndex(groups: CodeItemGroup[], gIdx: number): CodeItemGroup[] {
  if (gIdx < 0 || gIdx >= groups.length) return groups
  const removed = groups[gIdx]
  const removedRange = removed.codeItemPos.posRange
  const removedLen = Math.max(1, removedRange[1] - removedRange[0] + 1)
  const next: CodeItemGroup[] = []
  groups.forEach((group, idx) => {
    if (idx === gIdx) return
    if (idx > gIdx) {
      const [start, end] = group.codeItemPos.posRange
      const newStart = start - removedLen
      const newEnd = end - removedLen
      const newRange: [number, number] = [newStart, newEnd]
      next.push({
        ...group,
        codeItemPos: { ...group.codeItemPos, posRange: newRange, label: `${group.codeItemPos.code} [${newRange[0]}-${newRange[1]}]` },
      })
    } else {
      next.push(group)
    }
  })
  return next
}

function formatCodeItemNumber(position: number): string {
  return String(Math.max(1, position)).padStart(2, '0')
}

function getReindexedCode(currentCode: string, position: number): string {
  const nextNumber = formatCodeItemNumber(position)
  return /^c/i.test(String(currentCode || '')) ? `C${nextNumber}` : nextNumber
}

function replaceCodePrefix(value: string, previousCode: string, nextCode: string): string {
  if (!value || previousCode === nextCode) return value
  if (!value.startsWith(`${previousCode}-`)) return value
  return `${nextCode}${value.slice(previousCode.length)}`
}

function reindexTemporaryRowIdentifiers(row: CodeRow, previousCode: string, nextCode: string): CodeRow {
  const persistedRowId = typeof row.id === 'string' ? row.id.trim() : ''
  if (persistedRowId) return row

  const nextRowId = replaceCodePrefix(row.rowId, previousCode, nextCode)
  const nextDescriptionBlockId = replaceCodePrefix(row.descriptionBlockId, previousCode, nextCode)
  const nextAvailabilityBlockId = replaceCodePrefix(row.availabilityBlockId, previousCode, nextCode)

  if (
    nextRowId === row.rowId &&
    nextDescriptionBlockId === row.descriptionBlockId &&
    nextAvailabilityBlockId === row.availabilityBlockId
  ) {
    return row
  }

  return {
    ...row,
    rowId: nextRowId,
    descriptionBlockId: nextDescriptionBlockId,
    availabilityBlockId: nextAvailabilityBlockId,
  }
}

function reindexConditionGroupReferences(group: IfNode['conditionGroup'], codeMap: Map<string, string>): IfNode['conditionGroup'] {
  if (!group) return group

  return {
    ...group,
    conditions: group.conditions
      ? group.conditions.map(condition => ({
          ...condition,
          reference: condition.reference ? (codeMap.get(condition.reference) || condition.reference) : condition.reference,
        }))
      : group.conditions,
    groups: group.groups
      ? group.groups
          .map(subgroup => reindexConditionGroupReferences(subgroup, codeMap))
          .filter((subgroup): subgroup is ConditionGroup => Boolean(subgroup))
      : group.groups,
  }
}

function reindexIfNodeReferences(node: IfNode, codeMap: Map<string, string>): IfNode {
  return {
    ...node,
    reference: node.reference ? (codeMap.get(node.reference) || node.reference) : node.reference,
    conditionGroup: reindexConditionGroupReferences(node.conditionGroup, codeMap),
    consequenceGroups: node.consequenceGroups?.map(group => reindexConditionGroupReferences(group, codeMap)).filter((group): group is ConditionGroup => Boolean(group)) ?? node.consequenceGroups,
    if: node.if ? reindexIfNodeReferences(node.if, codeMap) : node.if,
    else: node.else ? reindexIfNodeReferences(node.else, codeMap) : node.else,
  }
}

function reindexRuleReferences(rule: CodeRow['rule'], codeMap: Map<string, string>): CodeRow['rule'] {
  if (!rule) return rule
  if (rule.type === 'while') {
    return {
      ...rule,
      reference: codeMap.get(rule.reference) || rule.reference,
    }
  }
  if (rule.type === 'text') return rule
  return reindexIfNodeReferences(rule, codeMap)
}

function ifNodeReferencesCode(node: IfNode, code: string): boolean {
  if (normalizeCodeItemReference(node.reference) === normalizeCodeItemReference(code)) return true
  if (node.conditionGroup && conditionGroupReferencesCode(node.conditionGroup, code)) return true
  if (node.consequenceGroups?.some(group => conditionGroupReferencesCode(group, code))) return true
  if (node.if && ifNodeReferencesCode(node.if, code)) return true
  if (node.else && ifNodeReferencesCode(node.else, code)) return true
  return false
}

function conditionGroupReferencesCode(group: IfNode['conditionGroup'], code: string): boolean {
  if (!group) return false
  if (group.conditions?.some(condition => normalizeCodeItemReference(condition.reference) === normalizeCodeItemReference(code))) return true
  if (group.groups?.some(subgroup => conditionGroupReferencesCode(subgroup, code))) return true
  return false
}

function ruleReferencesCode(rule: CodeRow['rule'], code: string): boolean {
  if (!rule) return false
  if (rule.type === 'while') return normalizeCodeItemReference(rule.reference) === normalizeCodeItemReference(code)
  if (rule.type === 'text') return false
  return ifNodeReferencesCode(rule, code)
}

function normalizeRuleReferenceValue(value: string | null | undefined): string {
  return String(value ?? '').trim()
}

function buildFallbackRuleLabel(rule: RuleNode): string {
  if (rule.type === 'while') {
    const reference = normalizeRuleReferenceValue(rule.reference)
    const condition = normalizeRuleReferenceValue(rule.condition)
    const value = normalizeRuleReferenceValue(rule.value)
    if (reference && condition && value) {
      return `While ${reference} ${condition} "${value}"`
    }

    return 'While rule'
  }

  if (rule.type === 'if') {
    const reference = normalizeRuleReferenceValue(rule.reference)
    const condition = normalizeRuleReferenceValue(rule.condition)
    const value = normalizeRuleReferenceValue(rule.value)

    if (reference && condition && value) {
      return `If ${reference} ${condition} "${value}"`
    }

    if (rule.conditionGroup) {
      return 'If condition group'
    }

    return 'If rule'
  }

  return 'Rule'
}

function getRuleLabel(rule: RuleNode): string {
  const description = normalizeRuleReferenceValue(rule.description)
  if (description) return description

  const id = normalizeRuleReferenceValue(rule.id)
  if (id) return id

  return buildFallbackRuleLabel(rule)
}

function visitConditionEntryValues(
  condition: ConditionEntry | null | undefined,
  owningRule: RuleNode,
  callback: (reference: string, value: string, rule: RuleNode, positionValues?: Record<string, string>) => void
) {
  if (!condition) return

  const reference = normalizeRuleReferenceValue(condition.reference)
  if (!reference) return

  if (condition.values && Object.keys(condition.values).length > 0) {
    const values = Object.fromEntries(
      Object.entries(condition.values)
        .filter(([key, value]) => String(key).trim() && normalizeRuleReferenceValue(value))
        .map(([key, value]) => [String(key), normalizeRuleReferenceValue(value).toUpperCase()])
    )
    if (Object.keys(values).length > 0) callback(reference, JSON.stringify(values), owningRule, values)
    return
  }

  const candidateValues = condition.condition === 'between'
    ? [condition.from, condition.to]
    : [condition.value]

  candidateValues.forEach((candidateValue) => {
    const value = normalizeRuleReferenceValue(candidateValue)
    if (!value) return

    callback(reference, value, owningRule)
  })
}

function visitConditionGroupValues(
  group: ConditionGroup | null | undefined,
  owningRule: RuleNode,
  callback: (reference: string, value: string, rule: RuleNode, positionValues?: Record<string, string>) => void
) {
  if (!group) return

  group.conditions?.forEach((condition) => {
    visitConditionEntryValues(condition, owningRule, callback)
  })

  group.groups?.forEach((subgroup) => {
    visitConditionGroupValues(subgroup, owningRule, callback)
  })
}

function visitIfNodeConditionValues(
  node: IfNode | null | undefined,
  owningRule: RuleNode,
  callback: (reference: string, value: string, rule: RuleNode, positionValues?: Record<string, string>) => void
) {
  if (!node) return

  const reference = normalizeRuleReferenceValue(node.reference)
  const value = normalizeRuleReferenceValue(node.value)
  if (reference && value) {
    callback(reference, value, owningRule)
  }

  visitConditionGroupValues(node.conditionGroup, owningRule, callback)
  node.consequenceGroups?.forEach((group) => {
    visitConditionGroupValues(group, owningRule, callback)
  })
  visitIfNodeConditionValues(node.if, owningRule, callback)
  visitIfNodeConditionValues(node.else, owningRule, callback)
}

function visitRuleConditionValues(
  rule: RuleNode | null | undefined,
  callback: (reference: string, value: string, rule: RuleNode, positionValues?: Record<string, string>) => void
) {
  if (!rule || rule.type === 'text') return

  if (rule.type === 'while') {
    const reference = normalizeRuleReferenceValue(rule.reference)
    const value = normalizeRuleReferenceValue(rule.value)
    if (reference && value) {
      callback(reference, value, rule)
    }
    return
  }

  visitIfNodeConditionValues(rule, rule, callback)
}

function buildCodeItemOptionsMap(codeItemsData: CodeItemReferenceOptions[]): Map<string, Set<string>> {
  return codeItemsData.reduce((accumulator, codeItem) => {
    const normalizedCode = normalizeCodeItemReference(codeItem.code)
    if (!normalizedCode) return accumulator

    accumulator.set(
      normalizedCode,
      new Set(
        (codeItem.options || [])
          .map((option) => normalizeRuleReferenceValue(option))
          .filter(Boolean)
      )
    )

    return accumulator
  }, new Map<string, Set<string>>())
}

function findPositionableReferencedOptions(
  reference: string,
  values: Record<string, string>,
  codeItemsData: CodeItemReferenceOptions[]
): { matchedOptions: string[]; fullyResolved: boolean } {
  const referencedCodeItem = codeItemsData.find(item => (
    normalizeCodeItemReference(item.code) === normalizeCodeItemReference(reference)
  ))
  if (!referencedCodeItem) return { matchedOptions: [], fullyResolved: false }

  const coveredPositions = new Set<string>()
  const matchedOptions = (referencedCodeItem.rows || []).flatMap((row) => {
    if (!isPositionedCodeRow(row)) return []
    const start = Number(row.valuePosition)
    const end = getCodeRowPositionEnd(row) ?? start
    const constrainedPositions = Object.keys(values).filter(key => {
      const position = Number(key)
      return Number.isInteger(position) && position >= start && position <= end
    })
    if (constrainedPositions.length === 0) return []

    const rowValues = buildPositionableValues(row.optionCode, start, end)
    const completeMatch = Object.entries(rowValues).every(([key, value]) => values[key] === value)
    if (!completeMatch || Object.keys(rowValues).length !== end - start + 1) return []

    Object.keys(rowValues).forEach(key => coveredPositions.add(key))
    return [normalizeRuleReferenceValue(row.optionCode)]
  })

  return {
    matchedOptions,
    fullyResolved: Object.keys(values).every(key => coveredPositions.has(key)),
  }
}

function formatPositionableMissingValue(values: Record<string, string>): string {
  return Object.entries(values)
    .sort(([left], [right]) => Number(left) - Number(right))
    .map(([position, value]) => `pos ${Number(position) + 1}=${value}`)
    .join(', ')
}

function pushUniqueRuleValueReferenceIssue(
  issues: RuleValueReferenceIssue[],
  issue: RuleValueReferenceIssue,
  seenKeys: Set<string>
) {
  const key = [
    issue.sourceCodeItemCode || '',
    issue.sourceOptionCode || '',
    issue.sourceRowId || '',
    String(issue.sourceGroupIndex ?? ''),
    String(issue.sourceRowIndex ?? ''),
    issue.referencedCodeItemCode,
    issue.missingValue,
    issue.ruleId || '',
    issue.ruleDescription || '',
  ].join('|||')

  if (seenKeys.has(key)) return

  seenKeys.add(key)
  issues.push(issue)
}

export function validateRuleValueReferences(
  rule: RuleNode | null | undefined,
  codeItemsData: CodeItemReferenceOptions[]
): RuleValueReferenceIssue[] {
  const issues: RuleValueReferenceIssue[] = []
  const validOptionsByCode = buildCodeItemOptionsMap(codeItemsData)
  const seenKeys = new Set<string>()

  visitRuleConditionValues(rule, (reference, value, owningRule, positionValues) => {
    if (positionValues) {
      const resolved = findPositionableReferencedOptions(reference, positionValues, codeItemsData)
      if (resolved.fullyResolved) return
      value = formatPositionableMissingValue(positionValues)
    }
    const validOptions = validOptionsByCode.get(normalizeCodeItemReference(reference))
    if (validOptions?.has(value)) return

    pushUniqueRuleValueReferenceIssue(issues, {
      referencedCodeItemCode: reference,
      missingValue: value,
      ruleId: normalizeRuleReferenceValue(owningRule.id) || null,
      ruleDescription: normalizeRuleReferenceValue(owningRule.description) || null,
      ruleLabel: getRuleLabel(owningRule),
    }, seenKeys)
  })

  return issues
}

export function findMissingRuleValueReferences(groups: CodeItemGroup[]): RuleValueReferenceIssue[] {
  const codeItemsData = groups.map((group) => ({
    code: group.codeItemPos.code,
    options: group.rows.map((row) => String(row.optionCode ?? '')),
    rows: group.rows,
  }))

  const validOptionsByCode = buildCodeItemOptionsMap(codeItemsData)
  const issues: RuleValueReferenceIssue[] = []
  const seenKeys = new Set<string>()

  groups.forEach((group, gIdx) => {
    group.rows.forEach((row, rIdx) => {
      visitRuleConditionValues(row.rule, (reference, value, owningRule, positionValues) => {
        if (positionValues) {
          const resolved = findPositionableReferencedOptions(reference, positionValues, codeItemsData)
          if (resolved.fullyResolved) return
          value = formatPositionableMissingValue(positionValues)
        }
        const validOptions = validOptionsByCode.get(normalizeCodeItemReference(reference))
        if (validOptions?.has(value)) return

        pushUniqueRuleValueReferenceIssue(issues, {
          referencedCodeItemCode: reference,
          missingValue: value,
          ruleId: normalizeRuleReferenceValue(owningRule.id) || null,
          ruleDescription: normalizeRuleReferenceValue(owningRule.description) || null,
          ruleLabel: getRuleLabel(owningRule),
          sourceCodeItemCode: group.codeItemPos.code,
          sourceOptionCode: normalizeRuleReferenceValue(row.optionCode),
          sourceRowId: row.rowId,
          sourceGroupIndex: gIdx,
          sourceRowIndex: rIdx,
        }, seenKeys)
      })
    })
  })

  return issues
}

export function findRuleValueUsages(
  groups: CodeItemGroup[],
  referencedCodeItemCode: string,
  referencedValue: string
): RuleValueReferenceIssue[] {
  const normalizedReferencedCodeItemCode = normalizeCodeItemReference(referencedCodeItemCode)
  const normalizedReferencedValue = normalizeRuleReferenceValue(referencedValue)

  if (!normalizedReferencedCodeItemCode || !normalizedReferencedValue) {
    return []
  }

  const issues: RuleValueReferenceIssue[] = []
  const seenKeys = new Set<string>()

  groups.forEach((group, gIdx) => {
    group.rows.forEach((row, rIdx) => {
      visitRuleConditionValues(row.rule, (reference, value, owningRule, positionValues) => {
        const targetGroup = groups.find(candidate => normalizeCodeItemReference(candidate.codeItemPos.code) === normalizedReferencedCodeItemCode)
        const targetRow = targetGroup?.rows.find(candidate => normalizeRuleReferenceValue(candidate.optionCode) === normalizedReferencedValue)
        const positionableMatch = Boolean(positionValues && targetRow && isPositionedCodeRow(targetRow) && (() => {
          const start = Number(targetRow.valuePosition)
          const end = getCodeRowPositionEnd(targetRow) ?? start
          const targetValues = buildPositionableValues(targetRow.optionCode, start, end)
          return Object.entries(targetValues).every(([key, targetValue]) => positionValues[key] === targetValue)
        })())
        if (
          normalizeCodeItemReference(reference) !== normalizedReferencedCodeItemCode
          || (!positionableMatch && value !== normalizedReferencedValue)
        ) {
          return
        }

        pushUniqueRuleValueReferenceIssue(issues, {
          referencedCodeItemCode: normalizedReferencedCodeItemCode,
          missingValue: normalizedReferencedValue,
          ruleId: normalizeRuleReferenceValue(owningRule.id) || null,
          ruleDescription: normalizeRuleReferenceValue(owningRule.description) || null,
          ruleLabel: getRuleLabel(owningRule),
          sourceCodeItemCode: group.codeItemPos.code,
          sourceOptionCode: normalizeRuleReferenceValue(row.optionCode),
          sourceRowId: row.rowId,
          sourceGroupIndex: gIdx,
          sourceRowIndex: rIdx,
        }, seenKeys)
      })
    })
  })

  return issues
}

export function reindexCodeItemGroups(groups: CodeItemGroup[]): CodeItemGroup[] {
  const codeMap = new Map<string, string>()

  groups.forEach((group, index) => {
    codeMap.set(group.codeItemPos.code, getReindexedCode(group.codeItemPos.code, index + 1))
  })

  return groups.map((group, index) => {
    const previousCode = group.codeItemPos.code
    const code = getReindexedCode(group.codeItemPos.code, index + 1)
    const [start, end] = group.codeItemPos.posRange

    return {
      ...group,
      codeItemPos: {
        ...group.codeItemPos,
        code,
        label: `${code} [${start}-${end}]`,
      },
      rows: group.rows.map(row => ({
        ...reindexTemporaryRowIdentifiers(row, previousCode, code),
        rule: reindexRuleReferences(row.rule, codeMap),
      })),
    }
  })
}

export function isCodeItemReferenced(groups: CodeItemGroup[], code: string): boolean {
  return groups.some(group => group.rows.some(row => ruleReferencesCode(row.rule, code)))
}

export function buildRowMapFromGroupIds<T>(rows: CodeRow[], column: 'description' | 'availability'): Array<{ rowSpan: number; value: T | null }> {
  const n = rows.length
  const map = new Array(n).fill(null).map(() => ({ rowSpan: 0, value: null as T | null }))
  let i = 0
  while (i < n) {
    const leaderBlockId = getBlockId(rows[i], column)
    let span = 1
    let j = i + 1
    while (j < n && getBlockId(rows[j], column) === leaderBlockId) {
      span++
      j++
    }
    const leaderValue = getColumnValue(rows[i], column)
    map[i] = { rowSpan: span, value: (leaderValue as unknown as T | null) ?? null }
    i = j
  }
  return map
}

export function doesBlockCoverRow(rows: CodeRow[], column: 'description' | 'availability', leaderIndex: number, targetIndex: number): boolean {
  const n = rows.length
  if (leaderIndex < 0 || leaderIndex >= n || targetIndex < 0 || targetIndex >= n) return false

  const leaderBlockId = getBlockId(rows[leaderIndex], column)
  if (leaderIndex > 0 && getBlockId(rows[leaderIndex - 1], column) === leaderBlockId) return false

  let blockEnd = leaderIndex
  while (blockEnd + 1 < n && getBlockId(rows[blockEnd + 1], column) === leaderBlockId) {
    blockEnd++
  }

  return targetIndex >= leaderIndex && targetIndex <= blockEnd
}

export function hasMergedSpanByGroupIds(rows: CodeRow[], column: 'description' | 'availability', start: number, end: number): boolean {
  const n = rows.length
  const s = Math.max(0, Math.min(start, end))
  const e = Math.min(n - 1, Math.max(start, end))
  let i = 0
  while (i < n) {
    const leaderBlockId = getBlockId(rows[i], column)
    let j = i + 1
    while (j < n && getBlockId(rows[j], column) === leaderBlockId) {
      j++
    }
    const blockStart = i
    const blockEnd = j - 1
    const len = blockEnd - blockStart + 1
    if (len > 1 && !(blockStart > e || blockEnd < s)) return true
    i = j
  }
  return false
}

